import { useState, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import "./App.css";
import { motion, AnimatePresence } from "motion/react";
import Folder from "./Folder";
import TiltedCard from "./TiltedCard";

function LikeButton({ liked, count, onClick, full }) {
  const [optimistic, setOptimistic] = useState(liked);
  const [optimisticCount, setOptimisticCount] = useState(count);
  const [showBurst, setShowBurst] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(() => { setOptimistic(liked); setOptimisticCount(count); }, [liked, count]);

  async function handleClick() {
    if (pending) return;
    const next = !optimistic;
    setOptimistic(next);
    setOptimisticCount(c => next ? c + 1 : Math.max(0, c - 1));
    if (next) {
      setShowBurst(true);
      setTimeout(() => setShowBurst(false), 700);
    }
    setPending(true);
    try {
      await onClick();
    } finally {
      setPending(false);
    }
  }

  return (
    <motion.button
      className={`lbv2${optimistic ? ' lbv2-on' : ''}${full ? ' lbv2-full' : ''}`}
      onClick={handleClick}
      disabled={pending}
      whileTap={{ scale: 0.88 }}
    >
      <span className="lbv2-inner">
        <motion.span
          className="lbv2-heart"
          animate={showBurst
            ? { scale: [0.6, 1.5, 0.9, 1.15, 1], transition: { duration: 0.55, times: [0, 0.22, 0.44, 0.68, 1] } }
            : { scale: 1 }}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 21.593c-5.63-5.539-11-10.297-11-14.402 0-3.791 3.068-5.191 5.281-5.191 1.312 0 4.151.501 5.719 4.457 1.59-3.968 4.464-4.447 5.726-4.447 2.54 0 5.274 1.621 5.274 5.181 0 4.069-5.136 8.625-11 14.402z"/>
          </svg>
        </motion.span>
        <span className="lbv2-count">{optimisticCount} {optimisticCount === 1 ? 'like' : 'likes'}</span>
      </span>
    </motion.button>
  );
}

const THEMES = [
  { id: "zinc",      name: "Zinc",      desc: "default",     bg: "#eef0f4", accent: "#1d4ed8" },
  { id: "graphite",  name: "Graphite",  desc: "warm amber",  bg: "#f2f0ec", accent: "#b45309" },
  { id: "copper",    name: "Copper",    desc: "industrial",  bg: "#f7f0e8", accent: "#c2410c" },
  { id: "terminal",  name: "Terminal",  desc: "hacker",      bg: "#080f08", accent: "#22c55e" },
  { id: "navy",      name: "Navy",      desc: "mil-spec",    bg: "#e8eef6", accent: "#1e3a5f" },
  { id: "sand",      name: "Sand",      desc: "desert",      bg: "#f0ebe0", accent: "#c2570a" },
  { id: "arctic",    name: "Arctic",    desc: "ice blue",    bg: "#f0f8ff", accent: "#0369a1" },
];

function ThemeToggle({ theme, setTheme }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = THEMES.find(t => t.id === theme) || THEMES[0];

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div className="theme-toggle-wrap" ref={ref}>
      <button className="theme-toggle-btn" onClick={() => setOpen(o => !o)}>
        <span className="theme-toggle-dot" style={{ background: current.accent }} />
        {current.name}
        <span style={{ opacity: 0.5 }}>▾</span>
      </button>
      {open && (
        <div className="theme-popover">
          <div className="theme-popover-label">Theme</div>
          {THEMES.map(t => (
            <button key={t.id} className={`theme-option ${theme === t.id ? "active" : ""}`}
              onClick={() => { setTheme(t.id); setOpen(false); }}>
              <div className="theme-option-swatch">
                <div className="theme-option-swatch-a" style={{ background: t.bg }} />
                <div className="theme-option-swatch-b" style={{ background: t.accent }} />
              </div>
              <div className="theme-option-info">
                <span className="theme-option-name">{t.name}</span>
                <span className="theme-option-desc">{t.desc}</span>
              </div>
              {theme === t.id && <span className="theme-option-check">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Lightbox({ srcs, idx, onClose }) {
  const [cur, setCur] = useState(idx ?? 0);
  const hasPrev = cur > 0;
  const hasNext = cur < srcs.length - 1;

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasPrev) setCur(c => c - 1);
      if (e.key === "ArrowRight" && hasNext) setCur(c => c + 1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hasPrev, hasNext, onClose]);

  return createPortal(
    <div className="lightbox-overlay" onClick={e => { e.stopPropagation(); onClose(); }}>
      <button className="lightbox-close" onClick={e => { e.stopPropagation(); onClose(); }}>×</button>
      {hasPrev && (
        <button className="lightbox-arrow lightbox-arrow--prev" onClick={e => { e.stopPropagation(); setCur(c => c - 1); }}>‹</button>
      )}
      <img
        key={cur}
        className="lightbox-img"
        src={srcs[cur]}
        alt=""
        onClick={e => e.stopPropagation()}
      />
      {hasNext && (
        <button className="lightbox-arrow lightbox-arrow--next" onClick={e => { e.stopPropagation(); setCur(c => c + 1); }}>›</button>
      )}
      {srcs.length > 1 && (
        <div className="lightbox-dots">
          {srcs.map((_, i) => (
            <button key={i} className={`lightbox-dot${i === cur ? " active" : ""}`}
              onClick={e => { e.stopPropagation(); setCur(i); }} />
          ))}
        </div>
      )}
    </div>,
    document.body
  );
}

const classInfo = {
  name: "Stakboard",
  teacher: "Mr. Krause",
  year: "2025–2026",
};

const badgeAsciiArt = {
  founder: ["/***\\\\", "|###|", "\\***/"],
  "first-project": ["/^^^\\\\", "|>-<|", "\\___/"],
  builder: ["[===]", " |-| ", "/___\\"],
  architect: [".---.", "|# #|", "'---'"],
  legend: ["_/^\\_", "\\===/", " /_\\ "],
  noticed: [" .-. ", "(o o)", " `-' "],
  hot: [" ( ) ", "(/\\\\)", " \\\\/ "],
  "fan-fav": [" /\\\\ ", "<><>", " \\\\/ "],
  social: [".---.", "|...|", "'-o-'"],
  influencer: ["|>>> ", "|===)", "|<<< "],
  rising: [" /!/ ", "/_!_\\", "  !  "],
  contributor: ["[III]", "| * |", "'---'"],
  achiever: ["[===]", "| + |", "'---'"],
  champion: ["[###]", "| * |", "'---'"],
  elite: [" .*.*", "< + >", " '*.*"],
};

function badgeShieldPoints(width, height, inset = 0) {
  const half = width / 2;
  const topY = 4 + inset;
  const shoulderY = 11 + inset * 0.7;
  const midY = height * 0.44;
  const kneeY = height * 0.78 - inset * 0.4;
  const bottomY = height - 4 - inset;
  const outerX = 5 + inset;
  const shoulderX = 11 + inset * 0.9;
  const waistX = half - (width * 0.18 - inset * 0.25);
  return [
    `${half},${topY}`,
    `${width - shoulderX},${shoulderY}`,
    `${width - outerX},${midY}`,
    `${width - waistX},${kneeY}`,
    `${half},${bottomY}`,
    `${waistX},${kneeY}`,
    `${outerX},${midY}`,
    `${shoulderX},${shoulderY}`,
  ].join(" ");
}

function BadgeIcon({ badge, color, accent, prestige = 1, variant }) {
  const lines = badgeAsciiArt[badge] || ["[???]", "[???]", "[???]"];
  const tier = Math.max(1, Math.min(prestige, 5));
  const width = variant === "showcase" ? 70 : 46;
  const height = variant === "showcase" ? 78 : 52;
  const startY = variant === "showcase" ? 29 : 20;
  const lineGap = variant === "showcase" ? 10.8 : 7.2;
  const innerInset = (variant === "showcase" ? 7 : 5) - (tier >= 4 ? 0.45 : 0);
  const glowRadius = (variant === "showcase" ? 18 : 10.5) + tier * (variant === "showcase" ? 3.2 : 2.6);
  const idBase = `${badge}-${variant}`.replace(/[^a-z0-9-]/gi, "-").toLowerCase();
  const shellGradientId = `badge-shell-${idBase}`;
  const coreGradientId = `badge-core-${idBase}`;
  const haloGradientId = `badge-halo-${idBase}`;
  const outerHaloGradientId = `badge-halo-outer-${idBase}`;
  const auraGradientId = `badge-aura-${idBase}`;
  const shellTopOpacity = [0.56, 0.68, 0.8, 0.92, 0.98][tier - 1];
  const coreWhiteOpacity = [0.12, 0.24, 0.48, 0.74, 0.96][tier - 1];
  const coreAccentOpacity = [0.08, 0.16, 0.28, 0.4, 0.46][tier - 1];
  const coreEdgeOpacity = [0.12, 0.14, 0.18, 0.22, 0.28][tier - 1];
  const matteSurfaceOpacity = [0.22, 0.12, 0.04, 0, 0][tier - 1];
  const showShine = tier >= 3;
  const showSideSpark = tier >= 4;
  const showTopSpark = tier >= 5;
  const iconStyle = {
    "--badge-halo-opacity": variant === "showcase" ? 0.04 + tier * 0.038 : 0.018 + tier * 0.032,
    "--badge-outer-halo-opacity": tier >= 4 ? 0.03 + (tier - 4) * 0.045 : 0,
    "--badge-aura-opacity": tier >= 3 ? 0.045 + (tier - 3) * 0.05 : 0,
    "--badge-shell-stroke": `${1.2 + tier * 0.18}px`,
    "--badge-core-stroke": `${0.55 + tier * 0.11}px`,
    "--badge-outline-opacity": `${0.12 + tier * 0.045}`,
    "--badge-outline-stroke": `${0.65 + tier * 0.08}px`,
    "--badge-shine-opacity": `${0.12 + tier * 0.06}`,
    "--badge-spark-opacity": `${tier >= 2 ? 0.52 + tier * 0.08 : 0}`,
    "--badge-spark-stroke": `${0.8 + tier * 0.12}px`,
    "--badge-ascii-tint": `${tier >= 4 ? 84 : tier === 3 ? 78 : 72}%`,
    filter: `drop-shadow(0 ${1 + tier}px ${3 + tier * 2}px color-mix(in srgb, var(--bc) ${5 + tier * 7}%, transparent)) drop-shadow(0 1px ${2 + tier}px rgba(15,23,42,${(0.08 + tier * 0.03).toFixed(2)}))`,
  };
  return (
    <span className="badge-chip-icon" aria-hidden="true" style={iconStyle}>
      <svg viewBox={`0 0 ${width} ${height}`} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id={shellGradientId} x1="16%" y1="8%" x2="84%" y2="92%">
            <stop offset="0%" stopColor={accent} stopOpacity={shellTopOpacity} />
            <stop offset="100%" stopColor={color} />
          </linearGradient>
          <radialGradient id={coreGradientId} cx="50%" cy="28%" r="72%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity={coreWhiteOpacity} />
            <stop offset="56%" stopColor={accent} stopOpacity={coreAccentOpacity} />
            <stop offset="100%" stopColor={color} stopOpacity={coreEdgeOpacity} />
          </radialGradient>
          <radialGradient id={haloGradientId} cx="50%" cy="42%" r="68%">
            <stop offset="0%" stopColor={accent} stopOpacity="0.34" />
            <stop offset="52%" stopColor={accent} stopOpacity="0.12" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </radialGradient>
          <radialGradient id={outerHaloGradientId} cx="50%" cy="44%" r="76%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.14" />
            <stop offset="38%" stopColor={accent} stopOpacity="0.08" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </radialGradient>
          <radialGradient id={auraGradientId} cx="50%" cy="36%" r="74%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.16" />
            <stop offset="48%" stopColor={accent} stopOpacity="0.09" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </radialGradient>
        </defs>
        {tier >= 3 && (
          <ellipse
            cx={width / 2}
            cy={height * 0.41}
            rx={width * (tier === 5 ? 0.34 : 0.31)}
            ry={height * (tier === 5 ? 0.23 : 0.2)}
            className="badge-chip-aura"
            fill={`url(#${auraGradientId})`}
          />
        )}
        <ellipse
          cx={width / 2}
          cy={height * 0.42}
          rx={glowRadius * 0.95}
          ry={glowRadius * 0.72}
          className="badge-chip-halo"
          fill={`url(#${haloGradientId})`}
        />
        {tier >= 4 && (
          <ellipse
            cx={width / 2}
            cy={height * 0.43}
            rx={glowRadius * (tier === 5 ? 1.24 : 1.12)}
            ry={glowRadius * (tier === 5 ? 0.88 : 0.8)}
            className="badge-chip-halo badge-chip-halo-outer"
            fill={`url(#${outerHaloGradientId})`}
          />
        )}
        <polygon points={badgeShieldPoints(width, height)} className="badge-chip-shell" fill={`url(#${shellGradientId})`} style={{ "--bc": color }} />
        <polygon points={badgeShieldPoints(width, height, innerInset)} className="badge-chip-core" fill={`url(#${coreGradientId})`} />
        {matteSurfaceOpacity > 0 && (
          <polygon
            points={badgeShieldPoints(width, height, innerInset + 1.2)}
            className="badge-chip-matte"
            style={{ "--bc": color, "--badge-matte-opacity": matteSurfaceOpacity }}
          />
        )}
        <polygon points={badgeShieldPoints(width, height, innerInset + 2.5)} className="badge-chip-outline" />
        {showShine && (
          <path
            className="badge-chip-shine"
            d={`M${width * 0.28} ${height * 0.19} C ${width * 0.4} ${height * 0.1}, ${width * 0.6} ${height * 0.1}, ${width * 0.72} ${height * 0.19} L ${width * 0.63} ${height * 0.29} C ${width * 0.54} ${height * 0.24}, ${width * 0.46} ${height * 0.24}, ${width * 0.37} ${height * 0.29} Z`}
          />
        )}
        {showSideSpark && (
          <path
            className="badge-chip-spark"
            style={{ "--ba": accent }}
            d={`M${width * 0.16} ${height * 0.18} v6 M${width * 0.16 - 3} ${height * 0.18 + 3} h6`}
          />
        )}
        {tier >= 4 && (
          <path
            className="badge-chip-spark"
            style={{ "--ba": accent }}
            d={`M${width * 0.82} ${height * 0.31} v4.5 M${width * 0.82 - 2.25} ${height * 0.31 + 2.25} h4.5`}
          />
        )}
        {showTopSpark && (
          <path
            className="badge-chip-spark"
            style={{ "--ba": accent }}
            d={`M${width * 0.5} ${height * 0.09} v5.5 M${width * 0.5 - 2.75} ${height * 0.09 + 2.75} h5.5`}
          />
        )}
        {lines.map((line, idx) => (
          <text key={`${badge}-${idx}`} x="50%" y={startY + idx * lineGap} textAnchor="middle" className="badge-chip-ascii">
            {line}
          </text>
        ))}
      </svg>
    </span>
  );
}

function BadgeChip({ badge, variant = "compact" }) {
  const info = badgeInfo[badge];
  if (!info) return null;
  const showCopy = variant === "showcase";
  return (
    <span
      className={`badge-chip badge-chip-${variant}`}
      title={`${info.label} - ${info.desc}`}
      aria-label={`${info.label} - ${info.desc}`}
      style={{ "--bc": info.color, "--ba": info.accent }}
    >
      <BadgeIcon badge={badge} color={info.color} accent={info.accent} prestige={info.prestige} variant={variant} />
      {showCopy && (
        <span className={`badge-chip-copy badge-chip-copy-${variant}`}>
          <span className="badge-chip-label">{info.label}</span>
        </span>
      )}
      <span className="badge-chip-tooltip" role="tooltip">
        <span className="badge-chip-tooltip-title">{info.label}</span>
        <span className="badge-chip-tooltip-desc">{info.desc}</span>
      </span>
    </span>
  );
}

function BadgeShowcase() {
  return (
    <section className="badge-showcase" aria-label="Available badges">
      <div className="badge-showcase-label">Badge Guide</div>
      <div className="badge-showcase-row">
        {Object.keys(badgeInfo).map((badge) => (
          <BadgeChip key={badge} badge={badge} variant="showcase" />
        ))}
      </div>
    </section>
  );
}

const badgeCategoryPalettes = {
  special: {
    5: { color: "#7f1d1d", accent: "#fde68a" }, // founder seal
  },
  projects: {
    1: { color: "#475569", accent: "#bfdbfe" }, // blueprint slate
    2: { color: "#315caa", accent: "#93c5fd" }, // steel blue
    3: { color: "#2563eb", accent: "#60a5fa" }, // cobalt
    4: { color: "#0f766e", accent: "#5eead4" }, // teal
    5: { color: "#155e75", accent: "#a5f3fc" }, // aurora cyan
  },
  likes: {
    1: { color: "#7c2d12", accent: "#fdba74" }, // ember ash
    2: { color: "#9a3412", accent: "#fb923c" }, // copper spark
    3: { color: "#c2410c", accent: "#fdba74" }, // blaze
    4: { color: "#b45309", accent: "#fde68a" }, // solar ember
    5: { color: "#9f1239", accent: "#fdba74" }, // inferno
  },
  comments: {
    1: { color: "#6b5b7a", accent: "#ddd6fe" }, // quiet mauve
    2: { color: "#7c3aed", accent: "#c4b5fd" }, // violet
    3: { color: "#a21caf", accent: "#f5d0fe" }, // orchid
    4: { color: "#be185d", accent: "#f9a8d4" }, // social pink
    5: { color: "#86198f", accent: "#f0abfc" }, // neon orchid
  },
  points: {
    1: { color: "#475569", accent: "#cbd5e1" }, // iron
    2: { color: "#9a5b22", accent: "#f2b980" }, // bronze
    3: { color: "#768397", accent: "#edf2f7" }, // silver
    4: { color: "#b7791f", accent: "#f6d365" }, // gold
    5: { color: "#6d28d9", accent: "#e9d5ff" }, // royal / mythic
  },
};

const badgeInfo = {
  // Special
  founder:         { label: "Founder",          desc: "StakLabs founding member",          prestige: 5, ...badgeCategoryPalettes.special[5] },
  // Projects posted
  "first-project": { label: "First Launch",     desc: "Posted your first project",         prestige: 1, ...badgeCategoryPalettes.projects[1] },
  builder:         { label: "Builder",          desc: "Posted 5 projects",                 prestige: 2, ...badgeCategoryPalettes.projects[2] },
  architect:       { label: "Architect",        desc: "Posted 10 projects",                prestige: 3, ...badgeCategoryPalettes.projects[3] },
  legend:          { label: "Legend",           desc: "Posted 20 projects",                prestige: 5, ...badgeCategoryPalettes.projects[5] },
  // Likes received
  noticed:         { label: "Getting Noticed",  desc: "Received 5 likes",                  prestige: 1, ...badgeCategoryPalettes.likes[1] },
  hot:             { label: "Hot Shot",         desc: "Received 25 likes",                 prestige: 3, ...badgeCategoryPalettes.likes[3] },
  "fan-fav":       { label: "Fan Favorite",     desc: "Received 50 likes",                 prestige: 4, ...badgeCategoryPalettes.likes[4] },
  // Comments received
  social:          { label: "Social",           desc: "Received 5 comments",               prestige: 2, ...badgeCategoryPalettes.comments[2] },
  influencer:      { label: "Influencer",       desc: "Received 20 comments",              prestige: 4, ...badgeCategoryPalettes.comments[4] },
  // Points milestones
  rising:          { label: "Rising Star",      desc: "Earned 50 points",                  prestige: 1, ...badgeCategoryPalettes.points[1] },
  contributor:     { label: "Contributor",      desc: "Earned 100 points",                 prestige: 2, ...badgeCategoryPalettes.points[2] },
  achiever:        { label: "Achiever",         desc: "Earned 250 points",                 prestige: 3, ...badgeCategoryPalettes.points[3] },
  champion:        { label: "Champion",         desc: "Earned 500 points",                 prestige: 4, ...badgeCategoryPalettes.points[4] },
  elite:           { label: "Elite",            desc: "Earned 1000 points",                prestige: 5, ...badgeCategoryPalettes.points[5] },
};

// ── API ───────────────────────────────────────────────────────────────────────

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

async function apiFetch(path, opts = {}) {
  const token = localStorage.getItem("token");
  const isFormData = opts.body instanceof FormData;
  const res = await fetch(`${API_BASE}/api${path}`, {
    headers: {
      ...(!isFormData ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...opts,
  });
  const text = await res.text();
  let data;
  try { data = JSON.parse(text); }
  catch { throw new Error(res.ok ? "Unexpected server response" : `Server error (${res.status})`); }
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

export function buildJobApplicationMailto(user = {}, form = {}) {
  const safeUser = user || {};
  const safeForm = form || {};
  const name = safeUser.name || "";
  const email = safeUser.email || "";
  const subject = `Job Application: ${name || email || "StakLabs Candidate"}`;
  const body = [
    "StakLabs Job Application",
    "",
    `Name: ${name}`,
    `Email: ${email}`,
    `Phone: ${safeForm.phone || ""}`,
    `Role Applied For: ${safeForm.role || ""}`,
    `Links / Portfolio: ${safeForm.links || ""}`,
    "",
    `Why I Want To Work At StakLabs: ${safeForm.why || ""}`,
  ].join("\n");

  return `mailto:info@staklabs.ai?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function parseGitHubRepo(url) {
  try {
    const parsed = new URL(url);
    if (!/(^|\.)github\.com$/i.test(parsed.hostname)) return null;
    const parts = parsed.pathname.split("/").filter(Boolean);
    if (parts.length < 2) return null;
    return { owner: parts[0], repo: parts[1] };
  } catch {
    return null;
  }
}

export function isStudentSignupEmail(email = "") {
  return /^[a-z][a-z]+\d{2}@student\.dasd\.org$/i.test(String(email).trim());
}

function normalizeStudent(s) {
  return {
    id:                s.id,
    userId:            s.user_id,
    name:              s.name,
    email:             s.email,
    points:            s.points,
    level:             s.level,
    projectsCompleted: s.projects_completed,
    tasksOnTime:       s.tasks_on_time,
    participation:     s.participation,
    streak:            s.streak,
    progress:          s.progress,
    group:             s.group_name,
    improvement:       s.improvement,
    recentActivity:    s.recent_activity,
    isFounder:         s.is_founder,
    badges:            s.badges || [],
  };
}

function parseImageUrls(value, fallback = "") {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch {}
  }
  return fallback ? [fallback] : [];
}

function normalizeProject(p) {
  const imageUrls = parseImageUrls(p.image_urls, p.image_url);
  return {
    ...p,
    image_url: imageUrls[0] || p.image_url || "",
    image_urls: imageUrls,
  };
}

function timeAgo(ts) {
  const d = (Date.now() - new Date(ts)) / 1000;
  if (d < 60)    return "just now";
  if (d < 3600)  return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const LEVEL_NAMES  = ["","Newcomer","Explorer","Builder","Maker","Innovator","Strategist","Leader","Visionary"];
const LEVEL_COLORS = ["","#94a3b8","#64748b","#10b981","#3b82f6","#8b5cf6","#f59e0b","#ef4444","#f97316"];
const lColor = (lv) => LEVEL_COLORS[lv] ?? "#635bff";
const lName  = (lv) => LEVEL_NAMES[lv]  ?? "Legend";

function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h;
}

function Initials({ name, size }) {
  const seed = fnv1a((name || '?').toLowerCase().trim());
  const hue  = seed % 360;
  const bg   = `hsl(${hue},68%,91%)`;
  const fg   = `hsl(${hue},55%,36%)`;

  // 5×5 symmetric grid — 3 unique cols, mirrored
  const S = 50; // internal viewBox units
  const GRID = 5, HALF = 3;
  const pad  = S * 0.14;
  const cell = (S - pad * 2) / GRID;

  const rects = [];
  for (let row = 0; row < GRID; row++) {
    for (let col = 0; col < HALF; col++) {
      if (!((seed >> (row * HALF + col)) & 1)) continue;
      const y = pad + row * cell;
      rects.push(<rect key={`${row}-${col}`}   x={pad + col * cell}           y={y} width={cell} height={cell} fill={fg} />);
      if (col < GRID - 1 - col)
        rects.push(<rect key={`${row}-m${col}`} x={pad + (GRID-1-col) * cell} y={y} width={cell} height={cell} fill={fg} />);
    }
  }

  return (
    <div className="avatar" style={size ? { width: size, height: size } : undefined}>
      <svg width="100%" height="100%" viewBox={`0 0 ${S} ${S}`} style={{ display: 'block' }}>
        <rect width={S} height={S} fill={bg} />
        {rects}
      </svg>
    </div>
  );
}

function ProgBar({ value, color = "#635bff", height = 6 }) {
  return (
    <div className="prog-track" style={{ height }}>
      <div className="prog-fill" style={{ width: `${Math.min(value, 100)}%`, background: color, height }} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Leaderboard (everyone sees this)
// ═══════════════════════════════════════════════════════════════════════════

function Leaderboard({ students }) {
  const [openStudentId, setOpenStudentId] = useState(null);

  if (!students.length) return (
    <div className="empty">No students yet.</div>
  );

  const MEDAL_LABELS = ["1st", "2nd", "3rd"];

  return (
    <div className="tab">
      <div className="card">
        <table className="lb-table">
          <thead>
            <tr>
              <th style={{ width: 48 }}>#</th>
              <th>Student</th>
              <th>Points</th>
              <th style={{ width: 160 }}>Progress</th>
              <th>Level</th>
              <th>Badges</th>
            </tr>
          </thead>
          <tbody>
            {students.flatMap((s, i) => {
              const isOpen = openStudentId === s.id;
              return [
                <tr
                  key={s.id}
                  className={`lb-row-clickable${isOpen ? " lb-row-open" : ""}`}
                  onClick={() => setOpenStudentId((id) => id === s.id ? null : s.id)}
                >
                  <td className="lb-rank-cell">
                    {i < 3
                      ? <span style={{ fontWeight: 700, color: i === 0 ? "#9a7000" : "var(--text2)" }}>{MEDAL_LABELS[i]}</span>
                      : <span style={{ color: "var(--text3)", fontWeight: 600 }}>{i + 1}</span>}
                  </td>
                  <td>
                    <div className="lb-name-cell">
                      <Initials name={s.name} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>
                          {s.name}
                          {s.isFounder && <span className="founder-tag">Founder</span>}
                        </div>
                        {s.group && <div style={{ fontSize: "0.7rem", color: "var(--text2)" }}>Group {s.group}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="lb-pts">{s.points.toLocaleString()}</td>
                  <td>
                    <ProgBar value={s.progress} color={lColor(s.level)} height={7} />
                  </td>
                  <td>
                    <span className="level-tag" style={{ color: lColor(s.level), borderColor: `${lColor(s.level)}40`, background: `${lColor(s.level)}10` }}>
                      Lv.{s.level} {lName(s.level)}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {s.badges.map((b) => <BadgeChip key={b} badge={b} />)}
                    </div>
                  </td>
                </tr>,
                isOpen ? (
                  <tr key={`${s.id}-detail`} className="lb-detail-row">
                    <td colSpan={6} className="lb-detail-cell">
                      <div className="student-detail">
                        <div className="detail-grid">
                          <div className="detail-stat">
                            <div className="detail-stat-v">{s.projectsCompleted}</div>
                            <div className="detail-stat-l">Projects</div>
                          </div>
                          <div className="detail-stat">
                            <div className="detail-stat-v">{s.tasksOnTime}</div>
                            <div className="detail-stat-l">On Time</div>
                          </div>
                          <div className="detail-stat">
                            <div className="detail-stat-v">{s.participation}</div>
                            <div className="detail-stat-l">Participation</div>
                          </div>
                          <div className="detail-stat">
                            <div className="detail-stat-v">{s.streak}</div>
                            <div className="detail-stat-l">Streak</div>
                          </div>
                        </div>

                        <div className="detail-row">
                          <span className="detail-label">Email</span>
                          <span className="detail-val">{s.email}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Group</span>
                          <span className="detail-val">{s.group || "Unassigned"}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Progress</span>
                          <span className="detail-val">{s.progress}%</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Improvement</span>
                          <span className="detail-val">{s.improvement}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Recent</span>
                          <span className="detail-val">{s.recentActivity || "No recent activity"}</span>
                        </div>

                        <div className="detail-row" style={{ alignItems: "flex-start", marginTop: 10 }}>
                          <span className="detail-label">Badges</span>
                          <div className="detail-badges">
                            {s.badges.length
                              ? s.badges.map((b) => <BadgeChip key={b} badge={b} />)
                              : <span className="detail-val">None yet</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : null,
              ];
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Feed (students post updates, everyone reads)
// ═══════════════════════════════════════════════════════════════════════════

function Feed({ user, posts, onPost }) {
  const [content, setContent]           = useState("");
  const [posting, setPosting]           = useState(false);
  const [localPosts, setLocalPosts]     = useState(posts);
  const [openReplyId, setOpenReplyId]   = useState(null);
  const [replies, setReplies]           = useState({});
  const [replyText, setReplyText]       = useState("");
  const [replyPosting, setReplyPosting] = useState(false);
  const [viewProj, setViewProj]         = useState(null);
  const [projCmts, setProjCmts]         = useState([]);
  const [projCmt, setProjCmt]           = useState("");
  const [projPosting, setProjPosting]   = useState(false);
  const [viewProjImage, setViewProjImage] = useState(0);
  const modalRef = useRef(null);

  useEffect(() => setLocalPosts(posts), [posts]);

  useEffect(() => {
    document.body.style.overflow = viewProj ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [viewProj]);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") setViewProj(null); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    function onDown(e) {
      if (e.target.closest(".lightbox-overlay")) return;
      if (modalRef.current && !modalRef.current.contains(e.target)) setViewProj(null);
    }
    if (viewProj) document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [viewProj]); // eslint-disable-line

  async function submit(e) {
    e.preventDefault();
    if (!content.trim()) return;
    setPosting(true);
    try {
      await apiFetch("/posts", { method: "POST", body: JSON.stringify({ content }) });
      setContent("");
      onPost();
    } catch (err) { console.error(err); }
    finally { setPosting(false); }
  }

  function openProject(post) {
    setViewProj({
      id: post.project_id,
      title: post.project_title,
      description: post.project_desc,
      image_url: post.project_image,
      image_urls: parseImageUrls(post.project_images, post.project_image),
      repo_url: post.project_repo,
      live_url: post.project_live,
      name: post.name,
      like_count: post.project_likes,
      liked_by_me: post.project_liked_by_me,
    });
    setViewProjImage(0);
    setProjCmts([]);
    setProjCmt("");
    apiFetch(`/projects/${post.project_id}/comments`).then(setProjCmts).catch(console.error);
  }

  async function toggleProjLike() {
    if (!viewProj) return;
    const res = await apiFetch(`/projects/${viewProj.id}/like`, { method: "POST" });
    setViewProj(prev => prev ? { ...prev, liked_by_me: res.liked, like_count: res.like_count } : prev);
    setLocalPosts(prev => prev.map(p => p.project_id === viewProj.id
      ? { ...p, project_liked_by_me: res.liked, project_likes: res.like_count } : p));
  }

  async function submitProjCmt(e) {
    e.preventDefault();
    if (!projCmt.trim() || !viewProj) return;
    setProjPosting(true);
    try {
      const c = await apiFetch(`/projects/${viewProj.id}/comments`, {
        method: "POST", body: JSON.stringify({ content: projCmt }),
      });
      setProjCmts(prev => [...prev, c]);
      setProjCmt("");
      setLocalPosts(prev => prev.map(p => p.project_id === viewProj.id
        ? { ...p, project_comments: (p.project_comments || 0) + 1 } : p));
    } catch (err) { console.error(err); }
    finally { setProjPosting(false); }
  }

  async function toggleReplies(postId) {
    if (openReplyId === postId) { setOpenReplyId(null); return; }
    setOpenReplyId(postId);
    setReplyText("");
    if (!replies[postId]) {
      try {
        const data = await apiFetch(`/posts/${postId}/replies`);
        setReplies(prev => ({ ...prev, [postId]: data }));
      } catch (err) { console.error(err); }
    }
  }

  async function submitReply(e, postId) {
    e.preventDefault();
    if (!replyText.trim()) return;
    setReplyPosting(true);
    try {
      const r = await apiFetch(`/posts/${postId}/replies`, {
        method: "POST", body: JSON.stringify({ content: replyText }),
      });
      setReplies(prev => ({ ...prev, [postId]: [...(prev[postId] || []), r] }));
      setLocalPosts(prev => prev.map(p => p.id === postId
        ? { ...p, reply_count: (p.reply_count || 0) + 1 } : p));
      setReplyText("");
    } catch (err) { console.error(err); }
    finally { setReplyPosting(false); }
  }

  return (
    <div className="tab">
      {/* Compose */}
      <div className="card">
        <form className="post-form" onSubmit={submit}>
          <Initials name={user.name} />
          <input className="post-input" placeholder="What are you working on?" value={content}
            onChange={e => setContent(e.target.value)} maxLength={280} />
          <button className="post-btn" type="submit" disabled={posting || !content.trim()}>Post</button>
        </form>
      </div>

      {/* Project modal */}
      <AnimatePresence>
        {viewProj && (
          <motion.div className="ec-backdrop"
            initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { duration: 0.15 } }}
            exit={{ opacity: 0, transition: { duration: 0.12 } }} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {viewProj && (
          <div className="ec-expanded-wrap">
            <motion.div ref={modalRef} className="ec-expanded"
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0, transition: { duration: 0.18, ease: [0.4, 0, 0.2, 1] } }}
              exit={{ opacity: 0, scale: 0.96, y: 10, transition: { duration: 0.12 } }}
            >
              <button className="proj-modal-x" onClick={() => setViewProj(null)}>×</button>
              <div className="ec-expanded-img">
                {viewProj.image_url
                  ? <img src={viewProj.image_urls?.[viewProjImage] || viewProj.image_url} alt={viewProj.title} className="ec-img" />
                  : <div className="ec-img-grad" style={{ background: `hsl(${(viewProj.id * 137) % 360}, 55%, 58%)` }} />
                }
              </div>
              <div className="ec-expanded-body">
                <div className="ec-expanded-header">
                  <div>
                    <h2 className="ec-title-big">{viewProj.title}</h2>
                    <div className="ec-meta">{viewProj.name}</div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {viewProj.live_url && (
                      <a href={viewProj.live_url} target="_blank" rel="noopener noreferrer" className="proj-repo-btn proj-preview-btn">Preview</a>
                    )}
                    {viewProj.repo_url && (
                      <a href={viewProj.repo_url} target="_blank" rel="noopener noreferrer" className="proj-repo-btn">GitHub</a>
                    )}
                  </div>
                </div>
                {viewProj.image_urls?.length > 1 && (
                  <div className="proj-gallery-strip">
                    {viewProj.image_urls.map((src, idx) => (
                      <button key={src} type="button" className={`proj-gallery-thumb${idx === viewProjImage ? " active" : ""}`} onClick={() => setViewProjImage(idx)}>
                        <img src={src} alt="" className="proj-gallery-thumb-img" />
                      </button>
                    ))}
                  </div>
                )}
                {viewProj.description && (
                  <p style={{ fontSize: "0.9rem", color: "var(--text)", lineHeight: 1.7, margin: 0 }}>{viewProj.description}</p>
                )}
                <div className="proj-comments-section">
                  <div className="proj-comments-label">
                    Comments {projCmts.length > 0 && <span className="proj-comments-count">{projCmts.length}</span>}
                  </div>
                  {projCmts.length > 0 && (
                    <div className="proj-comments-list">
                      {projCmts.map(c => (
                        <div key={c.id} className="proj-comment-item">
                          <Initials name={c.name} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--text)" }}>
                              {c.name}
                              <span style={{ fontWeight: 400, color: "var(--text3)", marginLeft: 6, fontSize: "0.68rem" }}>{timeAgo(c.created_at)}</span>
                            </div>
                            <div style={{ fontSize: "0.85rem", color: "var(--text)", marginTop: 2, lineHeight: 1.5 }}>{c.content}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <form className="proj-comment-form" onSubmit={submitProjCmt}>
                    <Initials name={user.name} />
                    <input className="post-input" placeholder="Leave a comment…" value={projCmt}
                      onChange={e => setProjCmt(e.target.value)} maxLength={280} />
                    <button className="post-btn" type="submit" disabled={projPosting || !projCmt.trim()}>Post</button>
                  </form>
                </div>
              </div>
              <div className="ec-like-bar">
                <LikeButton liked={!!viewProj.liked_by_me} count={viewProj.like_count || 0} onClick={toggleProjLike} full />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Feed */}
      {localPosts.length === 0 ? (
        <div className="empty">No updates yet. Be the first to post.</div>
      ) : (
        <div className="feed-list">
          {localPosts.map((p, i) => (
            <div className="feed-item" key={p.id}>
              <div className="feed-item-left">
                <Initials name={p.name} />
                {i < localPosts.length - 1 && <div className="feed-thread-line" />}
              </div>
              <div className="feed-item-right">
                <div className="feed-item-meta">
                  <span className="feed-item-name">{p.name}</span>
                  <span className="feed-item-time">{timeAgo(p.created_at)}</span>
                </div>

                {p.project_id ? (
                  <div className="feed-proj-card" onClick={() => openProject(p)}>
                    {p.project_image && <img src={p.project_image} className="feed-proj-img" alt="" />}
                    <div className="feed-proj-body">
                      <div className="feed-proj-title">{p.project_title}</div>
                      {p.project_desc && <div className="feed-proj-desc">{p.project_desc}</div>}
                      <div className="feed-proj-stats">
                        <span>{p.project_likes || 0} {(p.project_likes || 0) === 1 ? "like" : "likes"}</span>
                        <span className="feed-proj-dot">·</span>
                        <span>{p.project_comments || 0} comment{(p.project_comments || 0) !== 1 ? "s" : ""}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="feed-item-content">{p.content}</p>
                )}

                <div className="feed-item-actions">
                  <button className="feed-reply-btn" onClick={() => toggleReplies(p.id)}>
                    {openReplyId === p.id
                      ? "Hide"
                      : replies[p.id]?.length > 0
                        ? `${replies[p.id].length} repl${replies[p.id].length === 1 ? "y" : "ies"}`
                        : "Reply"}
                  </button>
                </div>

                {openReplyId === p.id && (
                  <div className="feed-replies">
                    {(replies[p.id] || []).map(r => (
                      <div className="feed-reply" key={r.id}>
                        <Initials name={r.name} />
                        <div className="feed-reply-body">
                          <div className="feed-reply-meta">
                            <span className="feed-reply-name">{r.name}</span>
                            <span className="feed-reply-time">{timeAgo(r.created_at)}</span>
                          </div>
                          <p className="feed-reply-content">{r.content}</p>
                        </div>
                      </div>
                    ))}
                    <form className="feed-reply-form" onSubmit={e => submitReply(e, p.id)}>
                      <Initials name={user.name} />
                      <input className="feed-reply-input" placeholder="Write a reply…" value={replyText}
                        onChange={e => setReplyText(e.target.value)} maxLength={280} autoFocus />
                      <button type="submit" className="post-btn feed-reply-post-btn"
                        disabled={replyPosting || !replyText.trim()}>Reply</button>
                    </form>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Admin (teacher only — simple)
// ═══════════════════════════════════════════════════════════════════════════

const QUICK_PTS = [10, 25, 50, 100];

function Admin({ students, posts, onRefresh }) {
  const [msg, setMsg] = useState({});

  async function addPoints(student, amount) {
    try {
      await apiFetch(`/students/${student.id}/points`, {
        method: "POST",
        body: JSON.stringify({ amount }),
      });
      setMsg((m) => ({ ...m, [student.id]: `+${amount} pts` }));
      setTimeout(() => setMsg((m) => { const n = { ...m }; delete n[student.id]; return n; }), 2000);
      onRefresh();
    } catch (err) {
      console.error(err);
    }
  }


  return (
    <div className="tab">

      {/* Students */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Students</span>
          <span className="card-sub">{students.length} enrolled</span>
        </div>
        {students.length === 0
          ? <div className="empty">No students have signed up yet.</div>
          : (
            <table className="lb-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Points</th>
                  <th>Award Points</th>
                  <th>Badges</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <div className="lb-name-cell">
                        <Initials name={s.name} />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>{s.name}</div>
                          <div style={{ fontSize: "0.7rem", color: "var(--text2)" }}>{s.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="lb-pts">{s.points.toLocaleString()}</span>
                      {msg[s.id] && (
                        <span style={{ marginLeft: 8, fontSize: "0.72rem", color: "var(--green)", fontWeight: 700 }}>
                          {msg[s.id]}
                        </span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 6 }}>
                        {QUICK_PTS.map((n) => (
                          <button key={n} className="pts-btn" onClick={() => addPoints(s, n)}>
                            +{n}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {s.badges.length === 0
                          ? <span style={{ fontSize: "0.7rem", color: "var(--text3)" }}>none yet</span>
                          : s.badges.map((b) => <BadgeChip key={b} badge={b} />)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {/* Recent posts */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Student Updates</span>
        </div>
        {posts.length === 0
          ? <div className="empty">No posts yet.</div>
          : posts.map((p) => (
            <div className="admin-post-row" key={p.id}>
              <Initials name={p.name} />
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 600, fontSize: "0.82rem" }}>{p.name}</span>
                <span style={{ color: "var(--text2)", fontSize: "0.82rem" }}> — {p.content}</span>
              </div>
              <span style={{ fontSize: "0.68rem", color: "var(--text3)", whiteSpace: "nowrap" }}>{timeAgo(p.created_at)}</span>
            </div>
          ))}
      </div>

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Projects — gallery (TiltedCard, all projects)
// ═══════════════════════════════════════════════════════════════════════════

const folderColor = (id) => `hsl(${(id * 137.508) % 360}, 62%, 42%)`;

const PROJ_GRADIENTS = [
  "linear-gradient(135deg,#635bff 0%,#9a7fff 100%)",
  "linear-gradient(135deg,#0e7490 0%,#22d3ee 100%)",
  "linear-gradient(135deg,#047857 0%,#34d399 100%)",
  "linear-gradient(135deg,#7c3aed 0%,#c084fc 100%)",
  "linear-gradient(135deg,#be185d 0%,#f472b6 100%)",
  "linear-gradient(135deg,#1d4ed8 0%,#60a5fa 100%)",
  "linear-gradient(135deg,#9a3412 0%,#fb923c 100%)",
];

function RepoViewerOverlay({ repoUrl, onClose }) {
  const parsed = parseGitHubRepo(repoUrl);
  const [path, setPath] = useState("");
  const [entries, setEntries] = useState([]);
  const [selectedFile, setSelectedFile] = useState("");
  const [fileContent, setFileContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadDir(nextPath = "") {
    if (!parsed) return;
    setLoading(true);
    setError("");
    setSelectedFile("");
    setFileContent("");
    try {
      const res = await fetch(`https://api.github.com/repos/${parsed.owner}/${parsed.repo}/contents/${nextPath}`);
      const data = await res.json();
      if (!res.ok || !Array.isArray(data)) throw new Error(data.message || "Failed to load repository");
      setEntries(data.sort((a, b) => (a.type === b.type ? a.name.localeCompare(b.name) : a.type === "dir" ? -1 : 1)));
      setPath(nextPath);
    } catch (err) {
      setError(err.message || "Failed to load repository");
    } finally {
      setLoading(false);
    }
  }

  async function openFile(entry) {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(entry.url);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to load file");
      const content = data.content ? atob(data.content.replace(/\n/g, "")) : "File could not be previewed.";
      setSelectedFile(entry.path);
      setFileContent(content);
    } catch (err) {
      setError(err.message || "Failed to load file");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDir("");
  }, [repoUrl]); // eslint-disable-line

  const canGoUp = path.includes("/");
  const parentPath = canGoUp ? path.split("/").slice(0, -1).join("/") : "";

  return (
    <div className="proj-modal-overlay" onClick={onClose}>
      <div className="repo-viewer-modal" onClick={e => e.stopPropagation()}>
        <button className="proj-modal-x" onClick={onClose}>×</button>
        <div className="repo-viewer-head">
          <div>
            <div className="job-apply-label">GitHub Viewer</div>
            <h2 className="job-apply-title">{parsed ? `${parsed.owner}/${parsed.repo}` : "Repository"}</h2>
            <p className="job-apply-copy">Browse the repo here without leaving the project viewer.</p>
          </div>
          <a href={repoUrl} target="_blank" rel="noopener noreferrer" className="proj-repo-btn">Open on GitHub</a>
        </div>
        {!parsed ? (
          <div className="empty">This GitHub URL could not be parsed.</div>
        ) : (
          <div className="repo-viewer-shell">
            <div className="repo-viewer-sidebar">
              <div className="repo-viewer-toolbar">
                <button type="button" className="repo-viewer-nav-btn" onClick={() => loadDir("")}>Root</button>
                {path && <button type="button" className="repo-viewer-nav-btn" onClick={() => loadDir(parentPath)}>Up</button>}
              </div>
              <div className="repo-viewer-path">/{path || parsed.repo}</div>
              {loading && entries.length === 0 ? <div className="empty">Loading repository…</div> : null}
              {error ? <div className="login-error">{error}</div> : null}
              <div className="repo-viewer-list">
                {entries.map((entry) => (
                  <button
                    key={entry.path}
                    type="button"
                    className={`repo-viewer-item${selectedFile === entry.path ? " active" : ""}`}
                    onClick={() => entry.type === "dir" ? loadDir(entry.path) : openFile(entry)}
                  >
                    <span className="repo-viewer-item-type">{entry.type === "dir" ? "DIR" : "FILE"}</span>
                    <span className="repo-viewer-item-name">{entry.name}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="repo-viewer-content">
              <div className="repo-viewer-filebar">{selectedFile || "Select a file to preview it here."}</div>
              <pre className="repo-viewer-code">{fileContent || "Choose a file from the left to open it in the built-in viewer."}</pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProjectModal({ p: initP, onClose, user, onLikeChange }) {
  const [p,          setP]          = useState(initP);
  const [comments,   setComments]   = useState([]);
  const [comment,    setComment]    = useState("");
  const [posting,    setPosting]    = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [lightbox,   setLightbox]   = useState(null);
  const [repoViewerUrl, setRepoViewerUrl] = useState(null);

  useEffect(() => setActiveImage(0), [p.id]);

  useEffect(() => {
    apiFetch(`/projects/${p.id}/comments`).then(setComments).catch(console.error);
  }, [p.id]); // eslint-disable-line

  async function toggleLike() {
    try {
      const res = await apiFetch(`/projects/${p.id}/like`, { method: "POST" });
      setP(prev => ({ ...prev, liked_by_me: res.liked, like_count: res.like_count }));
      if (onLikeChange) onLikeChange(p.id, res.liked, res.like_count);
    } catch (err) { console.error(err); }
  }

  async function postComment(e) {
    e.preventDefault();
    if (!comment.trim()) return;
    setPosting(true);
    try {
      const c = await apiFetch(`/projects/${p.id}/comments`, {
        method: "POST",
        body: JSON.stringify({ content: comment }),
      });
      setComments(prev => [...prev, c]);
      setComment("");
      setP(prev => ({ ...prev, comment_count: (prev.comment_count || 0) + 1 }));
    } catch (err) { console.error(err); }
    finally { setPosting(false); }
  }

  return (
    <div className="proj-modal-overlay" onClick={onClose}>
      <div className="proj-modal-v2" onClick={e => e.stopPropagation()}>
        <button className="proj-modal-x" onClick={onClose}>×</button>

        {lightbox && (
          <Lightbox srcs={p.image_urls?.length ? p.image_urls : [p.image_url]} idx={lightbox} onClose={() => setLightbox(null)} />
        )}
        {repoViewerUrl && (
          <RepoViewerOverlay repoUrl={repoViewerUrl} onClose={() => setRepoViewerUrl(null)} />
        )}
        {p.image_url ? (
          <div className="proj-modal-hero">
            <img src={p.image_urls?.[activeImage] || p.image_url} alt={p.title} className="proj-modal-hero-img"
              style={{ cursor: "zoom-in" }}
              onClick={() => setLightbox(activeImage)} />
            <div className="proj-modal-hero-grad" />
            <div className="proj-modal-hero-info">
              <h2 className="proj-modal-hero-title">{p.title}</h2>
              <div className="proj-modal-hero-by">by {p.name} · {timeAgo(p.created_at)}</div>
            </div>
          </div>
        ) : (
          <div className="proj-modal-no-img">
            <h2 className="proj-modal-hero-title" style={{ color: "var(--text)" }}>{p.title}</h2>
            <div style={{ fontSize: "0.78rem", color: "var(--text2)", marginTop: 4 }}>
              by <strong>{p.name}</strong> · {timeAgo(p.created_at)}
            </div>
          </div>
        )}

        <div className="proj-modal-v2-body">
          {p.image_urls?.length > 1 && (
            <div className="proj-gallery-strip">
              {p.image_urls.map((src, idx) => (
                <button key={src} type="button" className={`proj-gallery-thumb${idx === activeImage ? " active" : ""}`} onClick={() => setActiveImage(idx)}>
                  <img src={src} alt="" className="proj-gallery-thumb-img" />
                </button>
              ))}
            </div>
          )}
          {p.description && (
            <p className="proj-modal-v2-desc">{p.description}</p>
          )}

          <div className="proj-modal-actions">
            <LikeButton liked={!!p.liked_by_me} count={p.like_count || 0} onClick={toggleLike} />
            {p.live_url && (
              <a href={p.live_url} target="_blank" rel="noopener noreferrer" className="proj-repo-btn proj-preview-btn">Preview</a>
            )}
            {p.repo_url && (
              <button type="button" onClick={() => setRepoViewerUrl(p.repo_url)} className="proj-repo-btn">GitHub</button>
            )}
          </div>

          <div className="proj-comments-section">
            <div className="proj-comments-label">
              Comments {comments.length > 0 && <span className="proj-comments-count">{comments.length}</span>}
            </div>
            {comments.length > 0 && (
              <div className="proj-comments-list">
                {comments.map(c => (
                  <div key={c.id} className="proj-comment-item">
                    <Initials name={c.name} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--text)" }}>
                        {c.name}
                        <span style={{ fontWeight: 400, color: "var(--text3)", marginLeft: 6, fontSize: "0.68rem" }}>
                          {timeAgo(c.created_at)}
                        </span>
                      </div>
                      <div style={{ fontSize: "0.85rem", color: "var(--text)", marginTop: 2, lineHeight: 1.5 }}>
                        {c.content}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <form className="proj-comment-form" onSubmit={postComment}>
              <Initials name={user.name} />
              <input
                className="post-input"
                placeholder="Leave a comment…"
                value={comment}
                onChange={e => setComment(e.target.value)}
                maxLength={280}
              />
              <button className="post-btn" type="submit" disabled={posting || !comment.trim()}>
                Post
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function Projects({ projects: propProjects, user }) {
  const id = useId();
  const ref = useRef(null);
  const [projects,  setProjects]  = useState(propProjects);
  const [active,    setActive]    = useState(null);
  const [comments,  setComments]  = useState([]);
  const [newCmt,    setNewCmt]    = useState("");
  const [posting,   setPosting]   = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [lightbox,   setLightbox]   = useState(null);
  const [repoViewerUrl, setRepoViewerUrl] = useState(null);

  useEffect(() => setProjects(propProjects), [propProjects]);

  // Lock scroll when expanded card is open
  useEffect(() => {
    if (active) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [active]);

  // Escape key closes
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape" && lightbox === null) close(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightbox]); // eslint-disable-line

  // Outside click closes
  useEffect(() => {
    function onPointerDown(e) {
      if (lightbox !== null) return;
      if (ref.current && !ref.current.contains(e.target)) close();
    }
    if (active) document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [active, lightbox]); // eslint-disable-line

  function open(p) {
    setActive(p);
    setActiveImage(0);
    setComments([]);
    setNewCmt("");
    apiFetch(`/projects/${p.id}/comments`).then(setComments).catch(console.error);
  }

  function close() {
    setActive(null);
    setComments([]);
    setNewCmt("");
  }

  async function toggleLike() {
    if (!active) return;
    try {
      const res = await apiFetch(`/projects/${active.id}/like`, { method: "POST" });
      setActive(prev => prev ? { ...prev, liked_by_me: res.liked, like_count: res.like_count } : prev);
      setProjects(prev => prev.map(p => p.id === active.id ? { ...p, liked_by_me: res.liked, like_count: res.like_count } : p));
    } catch (err) { console.error(err); }
  }

  async function postComment(e) {
    e.preventDefault();
    if (!newCmt.trim() || !active) return;
    setPosting(true);
    try {
      const c = await apiFetch(`/projects/${active.id}/comments`, {
        method: "POST",
        body: JSON.stringify({ content: newCmt }),
      });
      setComments(prev => [...prev, c]);
      setNewCmt("");
    } catch (err) { console.error(err); }
    finally { setPosting(false); }
  }

  return (
    <div className="tab">
      {/* Header row */}
      <div>
        <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "var(--text)" }}>Projects</div>
        <div style={{ fontSize: "0.78rem", color: "var(--text2)", marginTop: 3 }}>
          {projects.length} project{projects.length !== 1 ? "s" : ""} shared
        </div>
      </div>

      {/* Backdrop */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.15 } }}
            exit={{ opacity: 0, transition: { duration: 0.12 } }}
            className="ec-backdrop"
          />
        )}
      </AnimatePresence>

      {/* Expanded card */}
      <AnimatePresence>
        {active && (
          <div className="ec-expanded-wrap">
            <motion.div
              layoutId={`ec-card-${active.id}-${id}`}
              ref={ref}
              className="ec-expanded"
              transition={{ type: "tween", duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            >
              {/* Card-wide like flash */}
              {/* Close button */}
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { delay: 0.08 } }}
                exit={{ opacity: 0 }}
                className="proj-modal-x"
                onClick={close}
              >
                ×
              </motion.button>

              {/* Hero image / gradient */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { delay: 0.04, duration: 0.14 } }}
                exit={{ opacity: 0 }}
                className="ec-expanded-img"
              >
                {active.image_url
                  ? <img src={active.image_urls?.[activeImage] || active.image_url} alt={active.title} className="ec-img"
                      style={{ cursor: "zoom-in" }}
                      onClick={() => setLightbox(activeImage)} />
                  : <div className="ec-img-grad" style={{ background: PROJ_GRADIENTS[projects.findIndex(p => p.id === active.id) % PROJ_GRADIENTS.length] }} />
                }
                {lightbox !== null && (
                  <Lightbox srcs={active.image_urls?.length ? active.image_urls : [active.image_url]} idx={lightbox} onClose={() => setLightbox(null)} />
                )}
                {repoViewerUrl && (
                  <RepoViewerOverlay repoUrl={repoViewerUrl} onClose={() => setRepoViewerUrl(null)} />
                )}
              </motion.div>

              {/* Body */}
              <div className="ec-expanded-body">
                <div className="ec-expanded-header">
                  <div>
                    <h2 className="ec-title-big">{active.title}</h2>
                    <div className="ec-meta">
                      <Initials name={active.name} size={22} />
                      <span>{active.name} · {timeAgo(active.created_at)}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {active.live_url && (
                      <motion.a initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.1 } }}
                        exit={{ opacity: 0 }} href={active.live_url} target="_blank" rel="noopener noreferrer"
                        className="proj-repo-btn proj-preview-btn">Preview</motion.a>
                    )}
                    {active.repo_url && (
                      <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.1 } }}
                        exit={{ opacity: 0 }} type="button" onClick={() => setRepoViewerUrl(active.repo_url)}
                        className="proj-repo-btn">GitHub</motion.button>
                    )}
                  </div>
                </div>

                {/* Fading content */}
                <motion.div
                  className="ec-content-panel"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1, transition: { delay: 0.08, duration: 0.14 } }}
                  exit={{ opacity: 0, transition: { duration: 0.06 } }}
                >
                  {active.image_urls?.length > 1 && (
                    <div className="proj-gallery-strip">
                      {active.image_urls.map((src, idx) => (
                        <button key={src} type="button" className={`proj-gallery-thumb${idx === activeImage ? " active" : ""}`} onClick={() => setActiveImage(idx)}>
                          <img src={src} alt="" className="proj-gallery-thumb-img" />
                        </button>
                      ))}
                    </div>
                  )}
                  {active.description && (
                    <p style={{ fontSize: "0.9rem", color: "var(--text)", lineHeight: 1.7, margin: "0 0 16px" }}>
                      {active.description}
                    </p>
                  )}

                  {/* Comments */}
                  <div className="proj-comments-section">
                    <div className="proj-comments-label">
                      Comments {comments.length > 0 && <span className="proj-comments-count">{comments.length}</span>}
                    </div>
                    {comments.length > 0 && (
                      <div className="proj-comments-list">
                        {comments.map(c => (
                          <div key={c.id} className="proj-comment-item">
                            <Initials name={c.name} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--text)" }}>
                                {c.name}
                                <span style={{ fontWeight: 400, color: "var(--text3)", marginLeft: 6, fontSize: "0.68rem" }}>
                                  {timeAgo(c.created_at)}
                                </span>
                              </div>
                              <div style={{ fontSize: "0.85rem", color: "var(--text)", marginTop: 2, lineHeight: 1.5 }}>
                                {c.content}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <form className="proj-comment-form" onSubmit={postComment}>
                      <Initials name={user.name} />
                      <input
                        className="post-input"
                        placeholder="Leave a comment…"
                        value={newCmt}
                        onChange={e => setNewCmt(e.target.value)}
                        maxLength={280}
                      />
                      <button className="post-btn" type="submit" disabled={posting || !newCmt.trim()}>
                        Post
                      </button>
                    </form>
                  </div>
                </motion.div>
              </div>

              {/* Like bar — bottom of card */}
              <motion.div
                className="ec-like-bar"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { delay: 0.1 } }}
                exit={{ opacity: 0 }}
              >
                <LikeButton liked={!!active.liked_by_me} count={active.like_count || 0} onClick={toggleLike} full />
              </motion.div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Grid */}
      {projects.length === 0 ? (
        <div className="empty">No projects yet.</div>
      ) : (
        <div className="ec-grid">
          {projects.map((p, i) => (
            <motion.div
              layoutId={`ec-card-${p.id}-${id}`}
              key={p.id}
              onClick={() => open(p)}
              className="ec-card"
              style={{ animationDelay: `${i * 0.04}s` }}
              transition={{ type: "tween", duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            >
              <TiltedCard
                imageSrc={p.image_url || undefined}
                bgColor={PROJ_GRADIENTS[i % PROJ_GRADIENTS.length]}
                containerHeight="220px"
                containerWidth="100%"
                imageHeight="220px"
                imageWidth="100%"
                showTooltip={false}
                rotateAmplitude={12}
                scaleOnHover={1.05}
                showMobileWarning={false}
                displayOverlayContent={true}
                overlayContent={
                  <div style={{
                    position: "absolute", bottom: 0, left: 0, right: 0,
                    background: "linear-gradient(to top, rgba(0,0,0,0.78) 0%, transparent 100%)",
                    padding: "32px 12px 12px",
                    borderRadius: "0 0 14px 14px",
                  }}>
                    <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#fff", lineHeight: 1.3 }}>{p.title}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 5 }}>
                      <Initials name={p.name} size={20} />
                      <span style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.8)" }}>
                        {p.name}{p.like_count > 0 && <span style={{ marginLeft: 8 }}>♥ {p.like_count}</span>}
                      </span>
                    </div>
                  </div>
                }
              />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// My Projects — user's own projects + submit form
// ═══════════════════════════════════════════════════════════════════════════

function MyProjects({ user, projects: propProjects, onRefresh }) {
  const myId  = useId();
  const myRef = useRef(null);
  const myProjects = propProjects.filter(p => p.user_id === user.id);

  const [active,     setActive]     = useState(null);
  const [comments,   setComments]   = useState([]);
  const [newCmt,     setNewCmt]     = useState("");
  const [posting,    setPosting]    = useState(false);

  const [editing,     setEditing]     = useState(null);
  const [editTitle,   setEditTitle]   = useState("");
  const [editDesc,    setEditDesc]    = useState("");
  const [editRepo,    setEditRepo]    = useState("");
  const [editLive,    setEditLive]    = useState("");
  const [editFiles,   setEditFiles]   = useState([]);
  const [editPreviews, setEditPreviews] = useState([]);
  const [editErr,     setEditErr]     = useState("");
  const [editSaving,  setEditSaving]  = useState(false);

  const [showForm,     setShowForm]     = useState(false);
  const [title,        setTitle]        = useState("");
  const [desc,         setDesc]         = useState("");
  const [repoUrl,      setRepoUrl]      = useState("");
  const [liveUrl,      setLiveUrl]      = useState("");
  const [imageFiles,   setImageFiles]   = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [submitting,   setSubmitting]   = useState(false);
  const [formErr,      setFormErr]      = useState("");
  const [ptsToast,     setPtsToast]     = useState("");
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [lightbox,     setLightbox]     = useState(null);
  const [repoViewerUrl, setRepoViewerUrl] = useState(null);

  useEffect(() => {
    document.body.style.overflow = active ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [active]);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape" && lightbox === null) close(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightbox]); // eslint-disable-line

  useEffect(() => {
    function onPointerDown(e) {
      if (lightbox !== null) return;
      if (myRef.current && !myRef.current.contains(e.target)) close();
    }
    if (active) document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [active, lightbox]); // eslint-disable-line

  function open(p) {
    setActive(p);
    setActiveImageIdx(0);
    setComments([]);
    setNewCmt("");
    apiFetch(`/projects/${p.id}/comments`).then(setComments).catch(console.error);
  }

  function close() { setActive(null); setComments([]); setNewCmt(""); }

  async function toggleLike() {
    if (!active) return;
    try {
      const res = await apiFetch(`/projects/${active.id}/like`, { method: "POST" });
      setActive(prev => prev ? { ...prev, liked_by_me: res.liked, like_count: res.like_count } : prev);
    } catch (err) { console.error(err); }
  }

  async function postComment(e) {
    e.preventDefault();
    if (!newCmt.trim() || !active) return;
    setPosting(true);
    try {
      const c = await apiFetch(`/projects/${active.id}/comments`, {
        method: "POST", body: JSON.stringify({ content: newCmt }),
      });
      setComments(prev => [...prev, c]);
      setNewCmt("");
    } catch (err) { console.error(err); }
    finally { setPosting(false); }
  }

  function openEdit(p) {
    setEditing(p);
    setEditTitle(p.title);
    setEditDesc(p.description || "");
    setEditRepo(p.repo_url || "");
    setEditLive(p.live_url || "");
    setEditFiles([]);
    setEditPreviews(p.image_urls || (p.image_url ? [p.image_url] : []));
    setEditErr("");
  }

  async function saveEdit(e) {
    e.preventDefault();
    if (!editTitle.trim()) return;
    setEditSaving(true); setEditErr("");
    try {
      const fd = new FormData();
      fd.append("title", editTitle);
      fd.append("description", editDesc);
      fd.append("repo_url", editRepo);
      fd.append("live_url", editLive);
      editFiles.forEach((file) => fd.append("images", file));
      await apiFetch(`/projects/${editing.id}`, { method: "PATCH", body: fd });
      setEditing(null);
      onRefresh();
    } catch (err) { setEditErr(err.message); }
    finally { setEditSaving(false); }
  }

  function resetForm() {
    setTitle(""); setDesc(""); setRepoUrl(""); setLiveUrl("");
    setImageFiles([]); setImagePreviews([]); setFormErr("");
  }

  async function submit(e) {
    e.preventDefault();
    if (!title.trim()) return;
    setSubmitting(true); setFormErr("");
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("description", desc);
      fd.append("repo_url", repoUrl);
      fd.append("live_url", liveUrl);
      imageFiles.forEach((file) => fd.append("images", file));
      const result = await apiFetch("/projects", { method: "POST", body: fd });
      resetForm();
      setShowForm(false);
      if (result.pts_earned) {
        setPtsToast(`+${result.pts_earned} pts earned`);
        setTimeout(() => setPtsToast(""), 3500);
      }
      onRefresh();
    } catch (err) { setFormErr(err.message); }
    finally { setSubmitting(false); }
  }

  const activeIdx = active ? myProjects.findIndex(p => p.id === active.id) : 0;

  return (
    <div className="tab">

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "var(--text)" }}>My Projects</div>
            {ptsToast && <span className="pts-toast">{ptsToast}</span>}
          </div>
          <div style={{ fontSize: "0.78rem", color: "var(--text2)", marginTop: 3 }}>
            {myProjects.length} project{myProjects.length !== 1 ? "s" : ""} shared
          </div>
        </div>
        <button className="post-btn" onClick={() => { if (showForm) resetForm(); setShowForm(v => !v); }}>
          {showForm ? "Cancel" : "+ Add Project"}
        </button>
      </div>

      {/* Submit form */}
      {showForm && (
        <div className="card" style={{ animation: "slideUp 0.2s ease" }}>
          <div className="card-header"><span className="card-title">Share a Project</span></div>
          <form onSubmit={submit} style={{ padding: "20px", display: "flex", flexDirection: "column", gap: 14 }}>
            <div className="field">
              <label className="field-label">Project Title *</label>
              <input className="field-input" placeholder="My Awesome App"
                value={title} onChange={e => setTitle(e.target.value)} />
            </div>
            <div className="field">
              <label className="field-label">Description</label>
              <textarea className="field-input" placeholder="What does it do? What did you learn?"
                value={desc} onChange={e => setDesc(e.target.value)} rows={3} style={{ resize: "vertical" }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div className="field">
                <label className="field-label">GitHub URL</label>
                <input className="field-input" placeholder="https://github.com/..."
                  value={repoUrl} onChange={e => setRepoUrl(e.target.value)} />
              </div>
              <div className="field">
                <label className="field-label">Live Preview URL</label>
                <input className="field-input" placeholder="https://myapp.vercel.app"
                  value={liveUrl} onChange={e => setLiveUrl(e.target.value)} />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }}>
              <div className="field">
                <label className="field-label">Screenshots / Images</label>
                <label className="img-pick-label img-pick-label--multi">
                  {imagePreviews.length
                    ? <div className="img-pick-grid">{imagePreviews.map((src) => <img key={src} src={src} alt="preview" className="img-pick-preview" />)}</div>
                    : <span className="img-pick-placeholder">Click to attach images</span>}
                  <input type="file" accept="image/*" multiple style={{ display: "none" }} onChange={e => {
                    const files = Array.from(e.target.files || []);
                    setImageFiles(files);
                    setImagePreviews(files.map((file) => URL.createObjectURL(file)));
                  }} />
                </label>
              </div>
            </div>
            {formErr && <div className="login-error">{formErr}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button className="login-btn" type="submit" disabled={submitting || !title.trim()}
                style={{ width: "auto", padding: "9px 24px" }}>
                {submitting ? "Submitting…" : "Submit Project"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Backdrop */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.15 } }}
            exit={{ opacity: 0, transition: { duration: 0.12 } }}
            className="ec-backdrop"
          />
        )}
      </AnimatePresence>

      {/* Expanded card */}
      <AnimatePresence>
        {active && (
          <div className="ec-expanded-wrap">
            <motion.div
              layoutId={`my-card-${active.id}-${myId}`}
              ref={myRef}
              className="ec-expanded"
              transition={{ type: "tween", duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            >
              <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.08 } }}
                exit={{ opacity: 0 }} className="proj-modal-x" onClick={close}>×</motion.button>

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.04, duration: 0.14 } }}
                exit={{ opacity: 0 }} className="ec-expanded-img">
                {active.image_url
                  ? <img src={active.image_urls?.[activeImageIdx] || active.image_url} alt={active.title} className="ec-img"
                      style={{ cursor: "zoom-in" }}
                      onClick={() => setLightbox(activeImageIdx)} />
                  : <div className="ec-img-grad" style={{ background: PROJ_GRADIENTS[activeIdx % PROJ_GRADIENTS.length] }} />
                }
                {lightbox !== null && (
                  <Lightbox srcs={active.image_urls?.length ? active.image_urls : [active.image_url]} idx={lightbox} onClose={() => setLightbox(null)} />
                )}
                {repoViewerUrl && (
                  <RepoViewerOverlay repoUrl={repoViewerUrl} onClose={() => setRepoViewerUrl(null)} />
                )}
              </motion.div>

              <div className="ec-expanded-body">
                <div className="ec-expanded-header">
                  <div>
                    <h2 className="ec-title-big">{active.title}</h2>
                    <div className="ec-meta">
                      <Initials name={active.name} size={22} />
                      <span>{active.name} · {timeAgo(active.created_at)}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                    {active.live_url && (
                      <motion.a initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.1 } }}
                        exit={{ opacity: 0 }} href={active.live_url} target="_blank" rel="noopener noreferrer"
                        className="proj-repo-btn proj-preview-btn">Preview</motion.a>
                    )}
                    {active.repo_url && (
                      <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.1 } }}
                        exit={{ opacity: 0 }} type="button" onClick={() => setRepoViewerUrl(active.repo_url)}
                        className="proj-repo-btn">GitHub</motion.button>
                    )}
                    <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.1 } }}
                      exit={{ opacity: 0 }} className="proj-repo-btn"
                      style={{ background: "var(--bg)", color: "var(--text2)", borderColor: "var(--border)" }}
                      onClick={() => { close(); setTimeout(() => openEdit(active), 50); }}>
                      Edit
                    </motion.button>
                  </div>
                </div>

                <motion.div className="ec-content-panel" initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.08, duration: 0.14 } }}
                  exit={{ opacity: 0, transition: { duration: 0.06 } }}>
                  {active.image_urls?.length > 1 && (
                    <div className="proj-gallery-strip">
                      {active.image_urls.map((src, idx) => (
                        <button key={src} type="button" className={`proj-gallery-thumb${idx === activeImageIdx ? " active" : ""}`} onClick={() => setActiveImageIdx(idx)}>
                          <img src={src} alt="" className="proj-gallery-thumb-img" />
                        </button>
                      ))}
                    </div>
                  )}
                  {active.description && (
                    <p style={{ fontSize: "0.9rem", color: "var(--text)", lineHeight: 1.7, margin: "0 0 16px" }}>
                      {active.description}
                    </p>
                  )}
                  <div className="proj-comments-section">
                    <div className="proj-comments-label">
                      Comments {comments.length > 0 && <span className="proj-comments-count">{comments.length}</span>}
                    </div>
                    {comments.length > 0 && (
                      <div className="proj-comments-list">
                        {comments.map(c => (
                          <div key={c.id} className="proj-comment-item">
                            <Initials name={c.name} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--text)" }}>
                                {c.name}
                                <span style={{ fontWeight: 400, color: "var(--text3)", marginLeft: 6, fontSize: "0.68rem" }}>
                                  {timeAgo(c.created_at)}
                                </span>
                              </div>
                              <div style={{ fontSize: "0.85rem", color: "var(--text)", marginTop: 2, lineHeight: 1.5 }}>
                                {c.content}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <form className="proj-comment-form" onSubmit={postComment}>
                      <Initials name={user.name} />
                      <input className="post-input" placeholder="Leave a comment…"
                        value={newCmt} onChange={e => setNewCmt(e.target.value)} maxLength={280} />
                      <button className="post-btn" type="submit" disabled={posting || !newCmt.trim()}>Post</button>
                    </form>
                  </div>
                </motion.div>
              </div>

              <motion.div className="ec-like-bar" initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { delay: 0.1 } }} exit={{ opacity: 0 }}>
                <LikeButton liked={!!active.liked_by_me} count={active.like_count || 0} onClick={toggleLike} full />
              </motion.div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Folder grid */}
      {myProjects.length === 0 ? (
        <div className="empty">No projects yet. Add your first one above.</div>
      ) : (
        <div className="proj-grid">
          {myProjects.map((p, i) => (
            <motion.div
              layoutId={`my-card-${p.id}-${myId}`}
              key={p.id}
              onClick={() => open(p)}
              className="proj-card"
              style={{ animationDelay: `${i * 0.045}s`, cursor: "pointer" }}
              transition={{ type: "tween", duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            >
              <div className="proj-card-folder">
                <Folder
                  color={folderColor(p.id)}
                  size={1}
                  isOpen={active?.id === p.id}
                  onOpen={() => open(p)}
                  items={(p.image_urls?.length ? p.image_urls : [p.image_url]).filter(Boolean).slice(0, 3).map((src, idx) => (
                    <img
                      key={`${p.id}-${idx}-${src}`}
                      src={src}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 6 }}
                    />
                  ))}
                />
              </div>
              <div className="proj-card-info">
                <div className="proj-card-title">{p.title}</div>
                <div className="proj-card-author">{p.name}</div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="proj-modal-overlay" onClick={() => setEditing(null)}>
          <div className="proj-modal-v2" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <button className="proj-modal-x" onClick={() => setEditing(null)}>×</button>
            <div className="proj-modal-no-img" style={{ paddingBottom: 0 }}>
              <h2 className="proj-modal-hero-title" style={{ color: "var(--text)" }}>Edit Project</h2>
            </div>
            <div className="proj-modal-v2-body">
              <form onSubmit={saveEdit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div className="field">
                  <label className="field-label">Title *</label>
                  <input className="field-input" value={editTitle} onChange={e => setEditTitle(e.target.value)} />
                </div>
                <div className="field">
                  <label className="field-label">Description</label>
                  <textarea className="field-input" value={editDesc} onChange={e => setEditDesc(e.target.value)}
                    rows={3} style={{ resize: "vertical" }} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <div className="field">
                    <label className="field-label">GitHub URL</label>
                    <input className="field-input" placeholder="https://github.com/..." value={editRepo}
                      onChange={e => setEditRepo(e.target.value)} />
                  </div>
                  <div className="field">
                    <label className="field-label">Live Preview URL</label>
                    <input className="field-input" placeholder="https://myapp.vercel.app" value={editLive}
                      onChange={e => setEditLive(e.target.value)} />
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }}>
                  <div className="field">
                    <label className="field-label">Replace Images</label>
                    <label className="img-pick-label img-pick-label--multi">
                      {editPreviews.length
                        ? <div className="img-pick-grid">{editPreviews.map((src) => <img key={src} src={src} alt="preview" className="img-pick-preview" />)}</div>
                        : <span className="img-pick-placeholder">Click to change</span>}
                      <input type="file" accept="image/*" multiple style={{ display: "none" }}
                        onChange={e => {
                          const files = Array.from(e.target.files || []);
                          if (files.length) {
                            setEditFiles(files);
                            setEditPreviews(files.map((file) => URL.createObjectURL(file)));
                          }
                        }} />
                    </label>
                  </div>
                </div>
                {editErr && <div className="login-error">{editErr}</div>}
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button className="login-btn" type="submit" disabled={editSaving || !editTitle.trim()}
                    style={{ width: "auto", padding: "9px 24px" }}>
                    {editSaving ? "Saving…" : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Login
// ═══════════════════════════════════════════════════════════════════════════

function Login({ onLogin }) {
  const [mode,     setMode]     = useState("signin");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [name,     setName]     = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "signup" && !isStudentSignupEmail(email)) {
        throw new Error("Use your school email in the format firstinitiallastname##@student.dasd.org.");
      }

      const endpoint = mode === "signup" ? "/auth/signup" : "/auth/signin";
      const body = mode === "signup"
        ? { name, email, password }
        : { email, password };
      const data = await apiFetch(endpoint, { method: "POST", body: JSON.stringify(body) });
      localStorage.setItem("token", data.token);
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <img src="/staklabs-mark.svg" alt="StakLabs" className="login-logo-img" />
        <h1 className="login-title">
          {mode === "signin" ? "Sign in to your account" : "Create an account"}
        </h1>
        <p className="login-sub">
          {mode === "signin" ? "Welcome back to the ISSS Class Dashboard." : "Join the ISSS Class Dashboard."}
        </p>

        <form className="login-form" onSubmit={handleSubmit} noValidate>
          {mode === "signup" && (
            <div className="field">
              <label className="field-label">Full name</label>
              <input className="field-input" type="text" placeholder="Jordan Lee"
                value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            </div>
          )}
          <div className="field">
            <label className="field-label">School email</label>
            <input
              className={`field-input ${error && error.toLowerCase().includes("email") ? "field-input--error" : ""}`}
              type="email" placeholder="kkokinda01@student.dasd.org"
              value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }} autoComplete="email" />
          </div>
          <div className="field">
            <label className="field-label" style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Password</span>
              {mode === "signin" && <span className="login-link" style={{ fontWeight: 400 }}>Forgot password?</span>}
            </label>
            <input className="field-input" type="password" placeholder="••••••••"
              value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }}
              autoComplete={mode === "signup" ? "new-password" : "current-password"} />
          </div>

          {error && <div className="login-error">{error}</div>}

          <button className="login-btn" type="submit" disabled={loading}>
            {loading ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
          </button>
        </form>

        <div className="login-switch">
          {mode === "signin"
            ? <>Don't have an account?{" "}<span className="login-link" onClick={() => { setMode("signup"); setError(""); }}>Sign up</span></>
            : <>Already have an account?{" "}<span className="login-link" onClick={() => { setMode("signin"); setError(""); }}>Sign in</span></>}
        </div>


      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// App Shell
// ═══════════════════════════════════════════════════════════════════════════

export default function App() {
  const [user,     setUser]     = useState(null);
  const [tab,      setTab]      = useState("leaderboard");
  const [students, setStudents] = useState([]);
  const [posts,    setPosts]    = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [theme,    setTheme]    = useState(() => localStorage.getItem("theme") || "zinc");
  const [showHackNote, setShowHackNote] = useState(false);
  const [showJobForm, setShowJobForm] = useState(false);
  const [jobForm, setJobForm] = useState({ phone: "", role: "", links: "", why: "" });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    apiFetch("/auth/me").then(setUser).catch(() => localStorage.removeItem("token"));
  }, []);

  function fetchData() {
    setLoading(true);
    Promise.allSettled([
      apiFetch("/students").then((rows) => rows.map(normalizeStudent)),
      apiFetch("/posts"),
      apiFetch("/projects").then((rows) => rows.map(normalizeProject)),
    ]).then(([s, p, pr]) => {
      if (s.status  === "fulfilled") setStudents(s.value);
      if (p.status  === "fulfilled") setPosts(p.value);
      if (pr.status === "fulfilled") setProjects(pr.value);
    }).finally(() => setLoading(false));
  }

  useEffect(() => { if (user) fetchData(); }, [user]); // eslint-disable-line

  useEffect(() => {
    if (user?.role === "teacher") setTab("admin");
  }, [user]); // eslint-disable-line

  function handleJobApplySubmit(e) {
    e.preventDefault();
    window.location.href = buildJobApplicationMailto(user, jobForm);
  }

  if (!user) return <Login onLogin={setUser} />;

  const isTeacher = user.role === "teacher";

  const STUDENT_TABS = [
    { id: "leaderboard",  label: "Leaderboard"  },
    { id: "feed",         label: "Feed"         },
    { id: "projects",     label: "Projects"     },
    { id: "my-projects",  label: "My Projects"  },
  ];
  const TEACHER_TABS = [
    { id: "admin",        label: "Admin"        },
    { id: "leaderboard",  label: "Leaderboard"  },
    { id: "feed",         label: "Feed"         },
    { id: "projects",     label: "Projects"     },
    { id: "my-projects",  label: "My Projects"  },
  ];
  const tabs = isTeacher ? TEACHER_TABS : STUDENT_TABS;

  const tabProps = { students, posts, projects, user, onRefresh: fetchData, onPost: fetchData };

  const Page = {
    leaderboard:  Leaderboard,
    feed:         Feed,
    admin:        Admin,
    projects:     Projects,
    "my-projects": MyProjects,
  }[tab];

  return (
    <div className="app">
      <header className="app-header">
        <div className="h-left">
          <img src="/staklabs-mark.svg" alt="StakLabs" className="h-logo-img" />
          <div className="h-sep" />
          <div>
            <div className="h-title">{classInfo.name}</div>

          </div>
        </div>
        <div className="h-center">
          <div className="hack-note-wrap">
            <button
              type="button"
              className="hack-link-btn"
              onClick={() => setShowHackNote(v => !v)}
            >
              Wanna try hacking the site?
            </button>
            {showHackNote && (
              <div className="hack-note-popover" role="dialog" aria-label="Hacking challenge details">
                <button type="button" className="hack-note-close" onClick={() => setShowHackNote(false)}>×</button>
                <div className="hack-note-title">Challenge Notice</div>
                <div className="hack-note-body">
                  There is a vulnerability in this site. If you exploit it and report it to the head of StakLabs or Mr. Krause, you can earn a special badge, official recognition for your skills, and a chance at a job with StakLabs.
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="h-right">
          <ThemeToggle theme={theme} setTheme={setTheme} />
          <span className="h-badge">{classInfo.year}</span>
          <div className="h-user">
            <Initials name={user.name} />
            <span className="h-user-email">{user.email}</span>
          </div>
          <button className="signout-btn" onClick={() => {
            localStorage.removeItem("token"); setUser(null); setStudents([]); setPosts([]); setProjects([]);
          }}>Sign out</button>
        </div>
      </header>

      <nav className="app-nav">
        {tabs.map((t) => (
          <button key={t.id} className={`nav-btn ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </nav>


      <main className="page">
        {loading
          ? <div className="empty">Loading…</div>
          : <Page {...tabProps} />}
      </main>

      {showJobForm && (
        <div className="proj-modal-overlay" onClick={() => setShowJobForm(false)}>
          <div className="job-apply-modal" onClick={e => e.stopPropagation()}>
            <button className="proj-modal-x" onClick={() => setShowJobForm(false)}>×</button>
            <div className="job-apply-head">
              <div className="job-apply-label">StakLabs Careers</div>
              <h2 className="job-apply-title">Apply for a job at StakLabs</h2>
              <p className="job-apply-copy">Fill this out and we’ll open an email draft to info@staklabs.ai with your profile and application details prefilled.</p>
            </div>
            <form className="job-apply-form" onSubmit={handleJobApplySubmit}>
              <div className="job-apply-grid">
                <label className="job-apply-field">
                  <span className="field-label">Name</span>
                  <input className="field-input" value={user.name || ""} readOnly />
                </label>
                <label className="job-apply-field">
                  <span className="field-label">Email</span>
                  <input className="field-input" value={user.email || ""} readOnly />
                </label>
                <label className="job-apply-field">
                  <span className="field-label">Phone</span>
                  <input className="field-input" value={jobForm.phone} onChange={e => setJobForm(f => ({ ...f, phone: e.target.value }))} placeholder="Best phone number" />
                </label>
                <label className="job-apply-field">
                  <span className="field-label">Role</span>
                  <input className="field-input" value={jobForm.role} onChange={e => setJobForm(f => ({ ...f, role: e.target.value }))} placeholder="What role do you want?" />
                </label>
              </div>
              <label className="job-apply-field">
                <span className="field-label">Links / Portfolio</span>
                <input className="field-input" value={jobForm.links} onChange={e => setJobForm(f => ({ ...f, links: e.target.value }))} placeholder="GitHub, portfolio, LinkedIn, etc." />
              </label>
              <label className="job-apply-field">
                <span className="field-label">Why should StakLabs hire you?</span>
                <textarea className="field-input job-apply-textarea" value={jobForm.why} onChange={e => setJobForm(f => ({ ...f, why: e.target.value }))} placeholder="Write your pitch here" required />
              </label>
              <div className="job-apply-actions">
                <button type="button" className="job-apply-cancel" onClick={() => setShowJobForm(false)}>Cancel</button>
                <button type="submit" className="job-apply-submit">Send application</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <footer className="app-footer">
        <button type="button" className="footer-text-link" onClick={() => setShowJobForm(true)}>
          Apply for a job at StakLabs
        </button>
        <span className="footer-sep">·</span>
        <a
          href="https://staklabs.ai"
          target="_blank"
          rel="noreferrer"
          className="footer-brand-link"
          aria-label="Visit StakLabs.ai"
        >
          <img src="/staklabslogo.png" alt="StakLabs" className="footer-logo" />
          <span>Built by StakLabs</span>
        </a>
        <span className="footer-sep">·</span>
        <span>A legacy gift for {classInfo.teacher} and the ISSS Class</span>
        <span className="footer-sep">·</span>
        <span>{classInfo.year}</span>
      </footer>
    </div>
  );
}
